import { BrowserRouter, Routes, Route, useParams, Navigate } from "react-router-dom";
import "@/App.css";
import { AuthProvider } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import MobileStickyCTA from "@/components/MobileStickyCTA";
import ProtectedRoute from "@/components/ProtectedRoute";
import ScrollToTop from "@/components/ScrollToTop";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Events from "@/pages/Events";
import EventDetail from "@/pages/EventDetail";
import VendorDashboard from "@/pages/VendorDashboard";
import OrganizerDashboard from "@/pages/OrganizerDashboard";
import CreateEvent from "@/pages/CreateEvent";
import VendorProfile from "@/pages/VendorProfile";
import VendorInquiries from "@/pages/VendorInquiries";
import VendorDocuments from "@/pages/VendorDocuments";
import PublicInvoice from "@/pages/PublicInvoice";
import PublicContract from "@/pages/PublicContract";
import FlyerLandingPage from "@/pages/FlyerLandingPage";
import PaymentSuccess from "@/pages/PaymentSuccess";
import Activate from "@/pages/Activate";
import PublicVendorProfile from "@/pages/PublicVendorProfile";
import Terms from "@/pages/Terms";
import Privacy from "@/pages/Privacy";
import Contact from "@/pages/Contact";
import AdminModeration from "@/pages/AdminModeration";
import AdminPromoCodes from "@/pages/AdminPromoCodes";
import AdminReconcile from "@/pages/AdminReconcile";
import AdminAnalytics from "@/pages/AdminAnalytics";
import AdminTeam from "@/pages/AdminTeam";
import AdminVerifications from "@/pages/AdminVerifications";
import AdminLaunchVideo from "@/pages/AdminLaunchVideo";
import Inbox from "@/pages/Inbox";
import InboxAdmin from "@/pages/InboxAdmin";
import BrowseVendors from "@/pages/BrowseVendors";
import AdminInbox from "@/pages/AdminInbox";
import AlertsSettings from "@/pages/AlertsSettings";
import VendorFavorites from "@/pages/VendorFavorites";
import TourTalk from "@/pages/TourTalk";
import VendTradeBrowse from "@/pages/vendtrade/VendTradeBrowse";
import VendTradeNew from "@/pages/vendtrade/VendTradeNew";
import VendTradeMine from "@/pages/vendtrade/VendTradeMine";
import VendTradeDetail from "@/pages/vendtrade/VendTradeDetail";
import VendAgainBrowse from "@/pages/vendagain/VendAgainBrowse";
import VendAgainNew from "@/pages/vendagain/VendAgainNew";
import VendAgainMine from "@/pages/vendagain/VendAgainMine";
import VendAgainDetail from "@/pages/vendagain/VendAgainDetail";
import VendAgainIsoBrowse from "@/pages/vendagain/VendAgainIsoBrowse";
import VendAgainIsoNew from "@/pages/vendagain/VendAgainIsoNew";
import VendAgainIsoMine from "@/pages/vendagain/VendAgainIsoMine";
import VendorTemplates from "@/pages/VendorTemplates";
import VendorAssistant from "@/pages/VendorAssistant";
import VendorBookkeeping from "@/pages/VendorBookkeeping";
import RateVendor from "@/pages/RateVendor";
import Referrals from "@/pages/Referrals";
import AdminUsers from "@/pages/AdminUsers";
import NonprofitVerification from "@/pages/NonprofitVerification";
import Nonprofits from "@/pages/Nonprofits";
import AdminNonprofits from "@/pages/AdminNonprofits";
import DonateSuccess from "@/pages/DonateSuccess";
import VolunteerSignup from "@/pages/VolunteerSignup";
import NonprofitDashboard from "@/pages/NonprofitDashboard";
import GiveBackDirectory from "@/pages/GiveBackDirectory";
import AdminUserDetail from "@/pages/AdminUserDetail";
import Pricing from "@/pages/Pricing";
import VendorBookings from "@/pages/VendorBookings";
import VendorEventMessages from "@/pages/VendorEventMessages";
import PublicBooking from "@/pages/PublicBooking";
import EventMessagesPage from "@/pages/EventMessagesPage";
import MyCalendarPage from "@/pages/MyCalendarPage";
import VolunteerDashboard from "@/pages/VolunteerDashboard";
import DonorDashboard from "@/pages/DonorDashboard";
import OrganizerVerify from "@/pages/OrganizerVerify";
import VendorVerify from "@/pages/VendorVerify";
import { useNativePush } from "@/lib/nativePush";

// React Router v6 can't reliably parse the pattern "/@:slug".
// Instead we match "/:handle" as a catch-all placed AFTER every specific route.
// If the segment starts with "@", we render the public vendor profile; else redirect home.
function HandleRoute() {
  const { handle } = useParams();
  if (!handle || !handle.startsWith("@") || handle.length < 2) {
    return <Navigate to="/" replace />;
  }
  return <PublicVendorProfile slugOverride={handle.slice(1)} />;
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ScrollToTop />
        <AppShell />
      </AuthProvider>
    </BrowserRouter>
  );
}

function AppShell() {
  // Native FCM push: bootstraps only inside the Capacitor iOS/Android shell.
  // On the web it's a no-op (Capacitor.isNativePlatform() === false).
  useNativePush();
  return (
    <div className="App min-h-screen flex flex-col">
      <Header />
      <main className="flex-1">
        <Routes>
              <Route path="/" element={<Landing />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/events" element={<Events />} />
              <Route path="/inbox" element={<ProtectedRoute><Inbox /></ProtectedRoute>} />
              <Route path="/inbox/admin" element={<ProtectedRoute><InboxAdmin /></ProtectedRoute>} />
              <Route path="/vendors" element={<ProtectedRoute><BrowseVendors /></ProtectedRoute>} />
              <Route path="/admin/inbox" element={<ProtectedRoute><AdminInbox /></ProtectedRoute>} />
              <Route path="/alerts" element={<ProtectedRoute><AlertsSettings /></ProtectedRoute>} />
              <Route path="/vendor/favorites" element={<ProtectedRoute><VendorFavorites /></ProtectedRoute>} />
              <Route path="/vendtrade" element={<VendTradeBrowse />} />
              <Route path="/vendtrade/new" element={<ProtectedRoute><VendTradeNew /></ProtectedRoute>} />
              <Route path="/vendtrade/mine" element={<ProtectedRoute><VendTradeMine /></ProtectedRoute>} />
              <Route path="/vendtrade/listings/:id" element={<VendTradeDetail />} />
              <Route path="/vendagain" element={<VendAgainBrowse />} />
              <Route path="/vendagain/new" element={<ProtectedRoute><VendAgainNew /></ProtectedRoute>} />
              <Route path="/vendagain/mine" element={<ProtectedRoute><VendAgainMine /></ProtectedRoute>} />
              <Route path="/vendagain/listings/:id" element={<VendAgainDetail />} />
              <Route path="/vendagain/listings/:id/edit" element={<ProtectedRoute><VendAgainNew /></ProtectedRoute>} />
              <Route path="/vendagain/iso" element={<VendAgainIsoBrowse />} />
              <Route path="/vendagain/iso/new" element={<ProtectedRoute><VendAgainIsoNew /></ProtectedRoute>} />
              <Route path="/vendagain/iso/mine" element={<ProtectedRoute><VendAgainIsoMine /></ProtectedRoute>} />
              <Route path="/vendagain/iso/:id/edit" element={<ProtectedRoute><VendAgainIsoNew /></ProtectedRoute>} />
              <Route path="/tourtalk" element={<TourTalk />} />
              <Route path="/nonprofits" element={<Nonprofits />} />
              <Route path="/events/:id" element={<EventDetail />} />
              <Route path="/activate" element={<ProtectedRoute><Activate /></ProtectedRoute>} />
              <Route path="/volunteer/dashboard" element={<ProtectedRoute><VolunteerDashboard /></ProtectedRoute>} />
              <Route path="/donor/dashboard" element={<ProtectedRoute><DonorDashboard /></ProtectedRoute>} />
              <Route path="/organizer/verify" element={<ProtectedRoute><OrganizerVerify /></ProtectedRoute>} />
              <Route path="/vendor/verify" element={<ProtectedRoute><VendorVerify /></ProtectedRoute>} />
              <Route
                path="/vendor/dashboard"
                element={<ProtectedRoute role="vendor"><VendorDashboard /></ProtectedRoute>}
              />
              <Route
                path="/vendor/profile"
                element={<ProtectedRoute role="vendor"><VendorProfile /></ProtectedRoute>}
              />
              <Route
                path="/vendor/inquiries"
                element={<ProtectedRoute role="vendor"><VendorInquiries /></ProtectedRoute>}
              />
              <Route
                path="/vendor/documents"
                element={<ProtectedRoute role="vendor"><VendorDocuments /></ProtectedRoute>}
              />
              <Route path="/invoice/:token" element={<PublicInvoice />} />
              <Route path="/contract/:token" element={<PublicContract />} />
              <Route
                path="/vendor/templates"
                element={<ProtectedRoute role="vendor"><VendorTemplates /></ProtectedRoute>}
              />
              <Route
                path="/vendor/assistant"
                element={<ProtectedRoute role="vendor"><VendorAssistant /></ProtectedRoute>}
              />
              <Route
                path="/vendor/bookkeeping"
                element={<ProtectedRoute role="vendor"><VendorBookkeeping /></ProtectedRoute>}
              />
              <Route
                path="/vendor/bookings"
                element={<ProtectedRoute role="vendor"><VendorBookings /></ProtectedRoute>}
              />
              <Route
                path="/vendor/event-messages"
                element={<ProtectedRoute role="vendor"><VendorEventMessages /></ProtectedRoute>}
              />
              <Route path="/book/:id" element={<PublicBooking />} />
              <Route
                path="/calendar"
                element={<ProtectedRoute><MyCalendarPage /></ProtectedRoute>}
              />
              <Route
                path="/organizer/events/:eventId/messages"
                element={<ProtectedRoute role="organizer"><EventMessagesPage /></ProtectedRoute>}
              />
              <Route
                path="/vendor/referrals"
                element={<ProtectedRoute role="vendor"><Referrals /></ProtectedRoute>}
              />
              <Route
                path="/organizer/referrals"
                element={<ProtectedRoute role="organizer"><Referrals /></ProtectedRoute>}
              />
              <Route
                path="/organizer/dashboard"
                element={<ProtectedRoute role="organizer"><OrganizerDashboard /></ProtectedRoute>}
              />
              <Route
                path="/organizer/events/new"
                element={<ProtectedRoute role="organizer"><CreateEvent /></ProtectedRoute>}
              />
              <Route path="/payment-success" element={<PaymentSuccess />} />
              <Route
                path="/admin/moderation"
                element={<ProtectedRoute role="organizer"><AdminModeration /></ProtectedRoute>}
              />
              <Route
                path="/admin/promo-codes"
                element={<ProtectedRoute role="organizer"><AdminPromoCodes /></ProtectedRoute>}
              />
              <Route
                path="/admin/reconcile"
                element={<ProtectedRoute role="organizer"><AdminReconcile /></ProtectedRoute>}
              />
              <Route
                path="/admin/analytics"
                element={<ProtectedRoute role="organizer"><AdminAnalytics /></ProtectedRoute>}
              />
              <Route
                path="/admin/users"
                element={<ProtectedRoute role="organizer"><AdminUsers /></ProtectedRoute>}
              />
              <Route
                path="/admin/users/:id"
                element={<ProtectedRoute role="organizer"><AdminUserDetail /></ProtectedRoute>}
              />
              <Route
                path="/admin/nonprofits"
                element={<ProtectedRoute requireAdmin><AdminNonprofits /></ProtectedRoute>}
              />
              <Route
                path="/admin/team"
                element={<ProtectedRoute requireAdmin><AdminTeam /></ProtectedRoute>}
              />
              <Route
                path="/admin/verifications"
                element={<ProtectedRoute requireAdmin><AdminVerifications /></ProtectedRoute>}
              />
              <Route
                path="/admin/launch-video"
                element={<ProtectedRoute requireAdmin><AdminLaunchVideo /></ProtectedRoute>}
              />
              <Route
                path="/verify-nonprofit"
                element={<ProtectedRoute><NonprofitVerification /></ProtectedRoute>}
              />
              <Route path="/vendors/:slug" element={<PublicVendorProfile />} />
              <Route path="/flyer/:eventId" element={<FlyerLandingPage />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/rate/:slug" element={<RateVendor />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/donate/success" element={<DonateSuccess />} />
              <Route path="/donate/cancel" element={<DonateSuccess />} />
              <Route path="/volunteer/:eventId" element={<VolunteerSignup />} />
              <Route path="/nonprofit/dashboard" element={<NonprofitDashboard />} />
              <Route path="/give-back" element={<GiveBackDirectory />} />
              {/* Catch-all last: matches "/@vendor-slug" style pretty URLs */}
              <Route path="/:handle" element={<HandleRoute />} />
            </Routes>
          </main>
          <Footer />
          <MobileStickyCTA />
          <Toaster position="top-right" richColors />
        </div>
  );
}
